// /fasting/learn — the education section. Phase 1 of docs/plan-fasting-groups.md.
//
// ⚠ READ THIS BEFORE EDITING ANY STRING BELOW.
//
// This is the one screen in the app that makes claims about a human body, and
// the rules it is written under were agreed with Will on 2026-09-10:
//
//   • THE STAGE RANGES ARE HIS OWN WORDING, from the plan, and they are stated
//     as RANGES rather than guarantees. Fasting physiology varies enormously --
//     somebody fat-adapted from keto reaches ketosis in hours, somebody coming
//     off a carb-heavy diet takes a day and feels terrible doing it. Every line
//     here hedges, and an edit that tightens a range into a promise is a
//     regression even if it reads better.
//   • THE PRACTICAL ITEMS ARE DELIBERATELY UNCONTROVERSIAL and sourced to
//     nobody: what a ketone is, that black coffee is generally considered fine
//     and a splash of milk generally is not, that people take electrolytes. The
//     plan names Ben Azadi's books as Will's own reference point; NOTHING from
//     them is reproduced or paraphrased here, and adding material from a
//     specific author is Will's to do with his own attribution.
//   • IT SAYS OUT LOUD THAT IT IS NOT MEDICAL ADVICE, once, at the top, without
//     a wall of legal text. Extended fasting genuinely is not safe for
//     everybody -- pregnancy, diabetes medication, eating-disorder history --
//     and a group app cannot know which reader it has.
//
// The route is NOT group-scoped, because none of this is group-specific.

import { el } from '../dom.js';
import { onLeave, type View } from '../router.js';
import { FAST_STAGES, KETOSIS_LABELS, KETOSIS_LEVELS } from '@msc/shared';

/** "0 to 4 hours", "24 hours and beyond". */
function bandLabel(fromHours: number, toHours: number | null): string {
  if (toHours === null) return `${fromHours} hours and beyond`;
  return `${fromHours} to ${toHours} hours`;
}

export const fastingLearnView: View = (outlet) => {
  const main = el('main', { class: 'page prose' });
  outlet.append(main);
  onLeave(() => undefined);

  main.replaceChildren(
    el('div', { class: 'page-head' }, [el('h1', {}, 'How fasting works')]),

    el('section', { class: 'card' }, [
      el(
        'p',
        {},
        'This is a rough guide so the numbers on your dial mean something. It is not ' +
          'medical advice, and extended fasting is not right for everyone. If you are ' +
          'pregnant, taking medication for diabetes, or have a history of disordered ' +
          'eating, talk to a doctor rather than to an app.',
      ),
    ]),

    el('section', { class: 'card' }, [
      el('h2', {}, 'The stages on the dial'),
      el(
        'p',
        { class: 'muted small' },
        // The single most important sentence on the page.
        'These are ranges, not guarantees. Where you actually are depends on what you ' +
          'ate, how long you have been eating that way, and you. Somebody already fat ' +
          'adapted from keto or regular intermittent fasting can reach ketosis within a ' +
          'few hours and skip much of the rough patch; somebody coming from a ' +
          'carb-heavy diet may take a day or more.',
      ),
      el(
        'dl',
        { class: 'stage-list' },
        FAST_STAGES.flatMap((stage) => [
          el('dt', {}, `${bandLabel(stage.fromHours, stage.toHours)}: ${stage.label}`),
          el('dd', {}, stage.detail),
        ]),
      ),
      el(
        'p',
        { class: 'muted small' },
        'If you log a ketosis reading before starting, the dial brings these bands ' +
          'forward a little, because being already in ketosis is most of what the early hours ' +
          'are for. That adjustment is a rough guide too.',
      ),
    ]),

    el('section', { class: 'card' }, [
      el('h2', {}, 'The rough patch'),
      el(
        'p',
        {},
        'Coming from a carb-heavy diet, a lot of people feel worst somewhere around the ' +
          'first day or two: headaches, tiredness, and a foggy head are the usual ' +
          'description, as the body changes which fuel it is running on. It tends to ' +
          'pass. People who fast regularly, or who eat keto, often barely notice it.',
      ),
    ]),

    el('section', { class: 'card' }, [
      el('h2', {}, 'What you can drink'),
      el(
        'ul',
        {},
        [
          'Water, as much as you like.',
          'Black coffee and plain tea are generally treated as fine.',
          'Milk, cream, sweeteners and anything with calories in it are generally not, ' +
            'a splash of milk is the commonest accidental end to a fast.',
          'Electrolytes (sodium, potassium, magnesium) are what many people reach for ' +
            'when they feel rough on a longer fast, and are worth reading about before ' +
            'you need them.',
        ].map((line) => el('li', {}, line)),
      ),
      el(
        'p',
        { class: 'muted small' },
        'Bone broth splits opinion: some treat it as fine on an extended fast, others ' +
          'use it only to get through a bad patch, and plenty would say it ends the ' +
          'fast. Worth agreeing with your group which of those you mean.',
      ),
    ]),

    el('section', { class: 'card' }, [
      el('h2', {}, 'Ketones, and testing for them'),
      el(
        'p',
        {},
        'Ketones are what your body makes from fat when it is not getting glucose, and ' +
          'they are the thing people mean by "being in ketosis". You can test for them ' +
          'with urine strips, which are cheap and approximate, with a blood meter, ' +
          'which is more accurate and more of a faff, or with a breath meter. Some ' +
          'continuous glucose monitors report on this too.',
      ),
      el('h3', {}, 'The three levels in this app'),
      el(
        'dl',
        { class: 'stage-list' },
        KETOSIS_LEVELS.flatMap((level) => [
          el('dt', {}, KETOSIS_LABELS[level].label),
          el('dd', {}, KETOSIS_LABELS[level].help),
        ]),
      ),
      el(
        'p',
        { class: 'muted small' },
        'Three buckets rather than a number, because reading a strip against a printed ' +
          'colour chart is genuinely hard and people disagree with themselves between ' +
          'readings. The app only uses it to nudge your dial, which does not need ' +
          'precision.',
      ),
    ]),

    el('section', { class: 'card' }, [
      el('h2', {}, 'Working up to a longer fast'),
      el(
        'p',
        {},
        'The usual progression is to push your first meal of the day later over days ' +
          'and weeks (eight, then nine, then ten in the morning, eventually early ' +
          'afternoon) rather than going straight to three days. Doing that alongside ' +
          'eating fewer carbs is what people mean by becoming fat adapted, and it is ' +
          'why the weeks before a group fast matter as much as the fast.',
      ),
      el(
        'p',
        { class: 'muted small' },
        'You can log ketosis readings during that whole run-up, not just during a fast.',
      ),
    ]),
  );
};
